/* eslint-disable react/prop-types */
import { useState } from 'react';
import { Store, Upload, Loader2, ArrowLeft, Trash2 } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import axios from 'axios';
import { useUser } from '../../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { API_URL, USER_ROLES } from '../../../constant/constant';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Header from '../../../components/layouts/Header/Header';
import BottomNav from '../../../components/layouts/Footer/BottomNav';
import axiosInstance from '../../../utils/axiosInstance';

function VendorMedia() {
  const { user, refreshUser } = useUser();
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedExistingImages, setSelectedExistingImages] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const MAX_FILE_SIZE_MB = 5;

  const handleFileUpload = (event) => {
    const filesToProcess = Array.from(event.target.files);
    const newValidFiles = [];

    filesToProcess.forEach((file) => {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        toast.error(`File "${file.name}" exceeds ${MAX_FILE_SIZE_MB}MB.`);
      } else {
        newValidFiles.push(file);
      }
    });
    setImages((prev) => [...prev, ...newValidFiles]);
  };

  const removeFile = (indexToRemove) => {
    setImages((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const toggleImageSelection = (imageUrl) => {
    setSelectedExistingImages((prev) =>
      prev.includes(imageUrl) ? prev.filter((url) => url !== imageUrl) : [...prev, imageUrl]
    );
  };

  const handleDeleteImages = async () => {
    if (!user?.id) {
      toast.error('Vendor information not available');
      return;
    }

    try {
      setShowDeleteModal(false);
      setIsDeleting(true);

      const sessionToken = localStorage.getItem('sessionToken');
      const url = `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/images?X-Vendor-Id=${user.id}`;

      await axiosInstance.delete(url, {
        headers: {
          sessionToken,
          'X-USER-ROLE': USER_ROLES.VENDOR,
          'Content-Type': 'application/json',
        },
        data: selectedExistingImages,
      });

      toast.success(`${selectedExistingImages.length} image(s) deleted successfully!`);
      setSelectedExistingImages([]);
      const contactNo = localStorage.getItem('contactNo');
      await refreshUser(contactNo, true);
    } catch (error) {
      console.error('Error deleting images:', error);
      toast.error('Failed to delete images. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmit = async () => {
    if (images.length === 0) {
      toast.warn('Please upload at least one new image to update.');
      return;
    }
    setIsSubmitting(true);
    const formData = new FormData();
    images.forEach((file) => formData.append('files', file));

    try {
      const sessionToken = localStorage.getItem('sessionToken'); // Mock token
      const url = `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/upload?X-Vendor-Id=${user?.id}`;

      await axiosInstance.post(url, formData, {
        headers: {
          sessionToken,
          'X-USER-ROLE': USER_ROLES.VENDOR,
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Media updated successfully!');
      const contactNo = localStorage.getItem('contactNo');
      refreshUser(contactNo, true);
      navigate(-1);
    } catch (error) {
      console.error('Error submitting vendor media:', error);
      toast.error('Error while saving media. This is a mock API call and will fail.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderImageGalleryUpload = () => (
    <Card>
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-3">
          <Store className="w-6 h-6 text-slate-600 flex-shrink-0" />
          <div>
            <label className="text-base font-semibold text-slate-800">Vendor Image Gallery</label>
            <p className="text-xs text-slate-500">
              Upload all vendor-related images here (Max {MAX_FILE_SIZE_MB}MB each).
            </p>
          </div>
        </div>
        <label
          htmlFor="image-gallery-upload"
          className="flex mb-3 max-h-36 flex-col items-center justify-center w-full aspect-square border-2 border-slate-300 border-dashed rounded-lg cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors"
        >
          <div className="flex flex-col items-center justify-center text-center p-2">
            <Upload className="w-8 h-8 text-slate-400 mb-2" />
            <p className="text-sm font-semibold text-slate-600">Add Images</p>
            <p className="text-xs text-slate-500">or drag & drop</p>
          </div>
          <input
            id="image-gallery-upload"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileUpload}
            multiple
          />
        </label>
        <div className="grid grid-cols-2 max-h-52 overflow-y-auto sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {images.map((file, index) => (
            <div key={index} className="relative aspect-square rounded-lg overflow-hidden group">
              <img
                src={URL.createObjectURL(file)}
                alt={`upload preview ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/20 transition-opacity flex items-center justify-center">
                <button
                  onClick={() => removeFile(index)}
                  className="bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-colors"
                  aria-label={`Remove image ${index + 1}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-100">
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-2 pb-20 md:pb-3">
        <div className="flex items-center gap-4 mb-2">
          <ArrowLeft onClick={() => navigate(-1)} className="cursor-pointer h-5 w-5" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Manage Media</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 mb-1">{renderImageGalleryUpload()}</div>

        {user?.vendorImages?.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3 mt-4">
              <h2 className="text-lg font-semibold text-slate-700">
                Current Gallery ({user.vendorImages.length})
              </h2>
              {selectedExistingImages.length > 0 && (
                <Button
                  onClick={() => setShowDeleteModal(true)}
                  disabled={isDeleting}
                  variant="destructive"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      Delete ({selectedExistingImages.length})
                    </>
                  )}
                </Button>
              )}
            </div>
            <div className="grid pb-8 max-h-72 overflow-y-auto grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {user.vendorImages.map((img, index) => {
                const isSelected = selectedExistingImages.includes(img);
                return (
                  <div key={index} className="relative">
                    {/* Checkbox */}
                    <div className="absolute top-2 right-2 z-10">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleImageSelection(img)}
                        className="w-5 h-5 cursor-pointer accent-green-600 bg-white border-2 border-slate-300 rounded shadow-sm"
                        aria-label={`Select image ${index + 1}`}
                      />
                    </div>

                    {/* Image */}
                    <div
                      onClick={() => toggleImageSelection(img)}
                      className={`aspect-square scale-95 rounded-lg overflow-hidden cursor-pointer transition-all ${
                        isSelected
                          ? 'ring-2 ring-green-500 shadow-lg'
                          : 'hover:ring-1 hover:ring-slate-300'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`vendor gallery ${index}`}
                        className="w-full h-full object-cover"
                      />
                      {isSelected && (
                        <div className="absolute inset-0 bg-green-500 bg-opacity-10" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="fixed left-0 right-0 md:left-64 lg:left-64 bg-white bottom-16 md:bottom-0 border-t border-slate-200 shadow-lg px-4 py-3 z-40">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={isSubmitting || isDeleting}
                className="w-full"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || isDeleting || images.length === 0}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-slate-300"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...
                  </>
                ) : (
                  'Update Media'
                )}
              </Button>
            </div>
          </div>
        </div>
      </main>
      <BottomNav />
      <ToastContainer position="bottom-right" autoClose={3000} hideProgressBar={false} />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
            </div>

            {/* Title */}
            <h3 className="text-xl font-bold text-slate-800 text-center mb-2">Delete Images?</h3>

            {/* Description */}
            <p className="text-slate-600 text-center mb-6">
              You are about to permanently delete{' '}
              <span className="font-semibold text-slate-900">{selectedExistingImages.length}</span>{' '}
              {selectedExistingImages.length === 1 ? 'image' : 'images'}. This action cannot be
              undone.
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteImages}
                disabled={isDeleting}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VendorMedia;
